/**
 * GW lookbook carousel.
 *
 * The storefront works without this module: the image rail remains a native
 * horizontal scroller and every hotspot remains a direct product anchor. This
 * layer adds buttons, keyboard movement, active-scene state and theme-editor
 * navigation without taking ownership of product clicks.
 */
class GwLookbook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.gwReady === 'true') return;

    this.viewport = this.querySelector('[data-lookbook-viewport]');
    this.track = this.querySelector('[data-lookbook-track]');
    this.slides = Array.from(this.querySelectorAll('[data-lookbook-slide]'));
    this.previousButton = this.querySelector('[data-lookbook-previous]');
    this.nextButton = this.querySelector('[data-lookbook-next]');
    this.arrows = this.querySelector('[data-lookbook-arrows]');
    this.navigation = this.querySelector('[data-lookbook-navigation]');
    this.sceneButtonsContainer = this.querySelector('[data-lookbook-scene-buttons]');
    this.sceneButtons = Array.from(this.querySelectorAll('[data-lookbook-scene-button]'));
    this.currentLabel = this.querySelector('[data-lookbook-current]');
    this.totalLabel = this.querySelector('[data-lookbook-total]');
    this.activeIndex = 0;
    this.scrollFrame = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pageTurn = null;
    this.pageTurnTimer = 0;
    this.pageTurnDuration = Number.parseInt(this.dataset.pageTurnDuration || '700', 10);

    if (!(this.viewport instanceof HTMLElement) || !(this.track instanceof HTMLElement) || !this.slides.length) {
      return;
    }

    const buttonMatchesSlide = (button, slide) => {
      const buttonBlockId = button.dataset.blockId || '';
      const slideBlockId = slide.dataset.blockId || '';
      const sameBlockIndex = button.dataset.blockIndex === slide.dataset.blockIndex;
      return sameBlockIndex || slideBlockId === buttonBlockId || slideBlockId.endsWith(`__${buttonBlockId}`);
    };
    const buttonsInRenderedOrder = this.slides
      .map((slide) => this.sceneButtons.find((button) => buttonMatchesSlide(button, slide)))
      .filter(Boolean);
    this.sceneButtons.forEach((button) => {
      if (!buttonsInRenderedOrder.includes(button)) button.remove();
    });
    this.sceneButtons = buttonsInRenderedOrder;
    this.sceneButtons.forEach((button, index) => {
      button.setAttribute('aria-controls', this.slides[index].id);
    });
    if (this.sceneButtonsContainer instanceof HTMLElement) {
      this.sceneButtonsContainer.append(...this.sceneButtons);
    }
    this.sceneButtons.forEach((button, index) => {
      button.dataset.slideIndex = String(index);
      button.setAttribute('aria-label', `Show scene ${index + 1} of ${this.slides.length}`);
      const number = button.querySelector('[aria-hidden="true"]');
      if (number) number.textContent = String(index + 1).padStart(2, '0');
    });
    if (this.totalLabel) this.totalLabel.textContent = String(this.slides.length);

    const hasSeveralScenes = this.slides.length > 1;
    if (this.arrows instanceof HTMLElement) this.arrows.hidden = !hasSeveralScenes;
    if (this.navigation instanceof HTMLElement) this.navigation.hidden = !hasSeveralScenes;

    this.onScroll = this.onScroll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
    this.onPrevious = () => this.select(this.activeIndex - 1);
    this.onNext = () => this.select(this.activeIndex + 1);
    this.sceneButtonHandlers = new Map();

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.onKeyDown);
    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);
    this.sceneButtons.forEach((button) => {
      const handler = () => this.select(Number(button.dataset.slideIndex || 0));
      this.sceneButtonHandlers.set(button, handler);
      button.addEventListener('click', handler);
    });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:block:select', this.onEditorBlockSelect);

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-label', `Scene ${index + 1} of ${this.slides.length}`);
    });

    this.updateState(0);
    this.dataset.gwReady = 'true';
  }

  disconnectedCallback() {
    delete this.dataset.gwReady;
    if (!(this.viewport instanceof HTMLElement)) return;
    this.viewport.removeEventListener('scroll', this.onScroll);
    this.viewport.removeEventListener('keydown', this.onKeyDown);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.sceneButtonHandlers?.forEach((handler, button) => button.removeEventListener('click', handler));
    this.sceneButtonHandlers?.clear();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.pageTurnTimer) window.clearTimeout(this.pageTurnTimer);
    this.pageTurn?.remove();
  }

  onScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateState(this.closestSlideIndex());
    });
  }

  onKeyDown(event) {
    if (event.target !== this.viewport) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = this.activeIndex + 1;
        break;
      case 'ArrowLeft':
        nextIndex = this.activeIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.slides.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(nextIndex);
  }

  onResize() {
    this.select(this.activeIndex, false);
  }

  onEditorBlockSelect(event) {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const selectedSlide = event.target.closest('[data-lookbook-slide]');
    if (!selectedSlide) return;
    const index = this.slides.indexOf(selectedSlide);
    if (index >= 0) this.select(index);
  }

  closestSlideIndex() {
    const viewportCenter = this.viewport.getBoundingClientRect().left + this.viewport.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  select(index, animate = true) {
    const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[safeIndex];
    if (!slide) return;

    const shouldTurnPage =
      animate &&
      safeIndex !== this.activeIndex &&
      this.dataset.pageTurn === 'true' &&
      !this.reducedMotion.matches;
    if (shouldTurnPage) this.turnPage(this.activeIndex, safeIndex);

    const left = slide.offsetLeft - this.track.offsetLeft;
    this.viewport.scrollTo({
      left,
      behavior: animate && !this.reducedMotion.matches && !shouldTurnPage ? 'smooth' : 'auto',
    });
    this.updateState(safeIndex);
  }

  turnPage(fromIndex, toIndex) {
    const sourceSlide = this.slides[fromIndex];
    const stage = this.querySelector('.gw-lookbook__stage');
    if (!(sourceSlide instanceof HTMLElement) || !(stage instanceof HTMLElement)) return;

    if (this.pageTurnTimer) window.clearTimeout(this.pageTurnTimer);
    this.pageTurn?.remove();

    const page = sourceSlide.cloneNode(true);
    page.removeAttribute('id');
    page.removeAttribute('data-lookbook-slide');
    page.removeAttribute('data-block-id');
    page.removeAttribute('data-block-index');
    page.removeAttribute('aria-label');
    page.className = `gw-lookbook__page-turn gw-lookbook__page-turn--${toIndex > fromIndex ? 'forward' : 'backward'}`;
    page.setAttribute('aria-hidden', 'true');
    page.setAttribute('inert', '');
    page.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    page.querySelectorAll('a, button, input, select, textarea').forEach((element) => element.setAttribute('tabindex', '-1'));

    const finish = () => {
      if (this.pageTurn === page) this.pageTurn = null;
      page.remove();
      if (this.pageTurnTimer) {
        window.clearTimeout(this.pageTurnTimer);
        this.pageTurnTimer = 0;
      }
    };

    page.addEventListener('animationend', finish, { once: true });
    stage.append(page);
    this.pageTurn = page;
    const safeDuration = Number.isFinite(this.pageTurnDuration) ? Math.min(1200, Math.max(300, this.pageTurnDuration)) : 700;
    this.pageTurnTimer = window.setTimeout(finish, safeDuration + 220);
  }

  updateState(index) {
    this.activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.sceneButtons.forEach((button, buttonIndex) => {
      if (buttonIndex === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    if (this.currentLabel) this.currentLabel.textContent = String(index + 1);
    if (this.previousButton) this.previousButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('gw-lookbook')) {
  customElements.define('gw-lookbook', GwLookbook);
}
